<script lang="ts">
	import { onMount } from "svelte";
	import ChevronLeftIcon from "@lucide/svelte/icons/chevron-left";
	import ChevronRightIcon from "@lucide/svelte/icons/chevron-right";
	import { getRentalTemplateContext } from "./rental-template-context.svelte";

	const templateContext = getRentalTemplateContext()!;

	let currentMonth = $state(new Date().getMonth());
	let currentYear = $state(new Date().getFullYear());
	const currentMonthName = $derived(
		new Date(currentYear, currentMonth, 1).toLocaleString("default", { month: "long" })
	);

	let monthEvents: typeof templateContext.events = $state([]);

	function filterEventsByMonth() {
		monthEvents = templateContext.events
			?.filter((event) => {
				const eventDate = new Date(event.date);
				return (
					eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear
				);
			})
			.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
	}

	function previousMonth() {
		if (currentMonth === 0) {
			currentMonth = 11;
			currentYear--;
		} else {
			currentMonth--;
		}
		filterEventsByMonth();
	}

	function nextMonth() {
		if (currentMonth === 11) {
			currentMonth = 0;
			currentYear++;
		} else {
			currentMonth++;
		}
		filterEventsByMonth();
	}

	function formatDate(dateString: string): string {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", { weekday: "short", day: "numeric" });
	}

	function formatTime(dateString: string): string {
		const date = new Date(dateString);
		return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
	}

	onMount(filterEventsByMonth);
</script>

<section id="events" class="bg-white">
	<div class="relative">
		<!-- Background image - takes up 40% of the width -->
		<div class="absolute left-0 h-full w-[20%] lg:w-[40%]">
			<img
				src={templateContext.eventImage}
				alt="Event"
				class="h-full w-full object-cover"
				width="800"
				height="600"
				loading="lazy"
			/>
		</div>

		<!-- Main content container -->
		<div class="container mx-auto py-16">
			<div class="grid grid-cols-10">
				<!-- Empty space to account for the image -->
				<div class="col-span-2 lg:col-span-4"></div>

				<!-- Content column -->
				<div class="col-span-6 p-6">
					<h2 class="mb-6 text-4xl font-medium text-slate-700">EVENT CALENDAR</h2>
					<hr class="border-primary mb-8 w-[100px] border-2" />

					<!-- Month navigation -->
					<div
						class="bg-primary mb-6 flex max-w-[300px] items-center justify-between rounded-full p-2"
					>
						<button
							onclick={previousMonth}
							class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
							aria-label="Previous month"
						>
							<ChevronLeftIcon class="h-5 w-5 text-slate-700" />
						</button>

						<div class="text-center">
							<h3 class="text-xl font-medium">{currentMonthName} {currentYear}</h3>
						</div>

						<button
							onclick={nextMonth}
							class="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
							aria-label="Next month"
						>
							<ChevronRightIcon class="h-5 w-5 text-slate-700" />
						</button>
					</div>

					<!-- Events list -->
					{#if monthEvents && monthEvents.length > 0}
						<ul class="divide-y divide-gray-200">
							{#each monthEvents as event (event.title)}
								<li class="flex py-4 hover:bg-gray-50">
									<div class="mr-4 w-16 shrink-0 text-center">
										<div class="text-sm text-gray-500 uppercase">
											{formatDate(event.date).split(" ")[1]}
										</div>
										<div class="text-3xl font-light">
											{formatDate(event.date).split(" ")[0]}
										</div>
										<div class="text-xs text-gray-500 uppercase">
											{currentMonthName}
										</div>
									</div>
									<div class="grow">
										<h4 class="font-medium text-slate-800">{event.title}</h4>
										<div class="mt-1 text-sm text-slate-600">
											{formatTime(event.date)}
											{#if event.description}
												• {event.description}
											{/if}
										</div>
									</div>
								</li>
							{/each}
						</ul>
					{:else}
						<div class="py-6 text-center text-slate-500">
							<p>No events scheduled for {currentMonthName}</p>
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
</section>
